/// <amd-module name="VSSUI/Components/VssDetailsList/VssDetailsList" />

import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import {
    CheckboxVisibility,
    DetailsList,
    IColumn,
    IDetailsList,
    IDetailsListProps,
    ISelection,
    Selection,
    SelectionMode
} from "OfficeFabric/DetailsList";
import { BaseComponent } from "OfficeFabric/Utilities";
import * as React from "react";
import "VSS/LoaderPlugins/Css!VSSUI/Components/VssDetailsList/VssDetailsList";
import { ContextualMenuButton } from "VSSUI/Components/ContextualMenuButton/ContextualMenuButton";
import {
    IVssDetailsList,
    IVssDetailsListProps,
    VssDetailsListPresentationStyles,
    VssDetailsListRowStyle
} from "VSSUI/Components/VssDetailsList/VssDetailsList.Props";
import { MoreActionsButton } from "VSSUI/ContextualMenuButton";
import { css, KeyCode } from "VSSUI/Util";
import { closest } from "VSSUI/Utilities/Internal";
import { VssContextualMenu } from "VSSUI/VssContextualMenu";

/** @deprecated */
export interface IVssDetailsListState {
    /**
     * The item in the list (if any) for which the context menu is currently active,
     * if it was activated via the onItemContextMenu for the DetailsList.
     */
    activatedItem?: any;

    /**
     * The mouse event or HTMLElement from the onContextMenu event where the
     * contextual menu should be displayed.
     */
    target?: MouseEvent | HTMLElement;

    /**
     * Classnames to pass to DetailsList based on presentationStyles provided
     * as props.
     */
    classNames?: string;

    /**
     * Columns to pass to DetailsList (this may not be the same as the one from props if context menu is enabled).
     */
    columns?: IColumn[];

    /**
     * Selection object for the DetailsList.
     */
    selection?: ISelection;
}

/**
 * Wraps Office Fabric Details list with state management specific to Vss Components
 */
/** @deprecated Please use import { List } from "VSSUI/List" */
export class VssDetailsList extends BaseComponent<IVssDetailsListProps, IVssDetailsListState> implements IVssDetailsList {
    private readonly standardPresentationStyle =
        VssDetailsListPresentationStyles.colors |
        VssDetailsListPresentationStyles.flexCellLayout |
        VssDetailsListPresentationStyles.headerRowStyles |
        VssDetailsListPresentationStyles.hideActionsButton |
        VssDetailsListPresentationStyles.linkStyles |
        VssDetailsListPresentationStyles.paddingInCells |
        VssDetailsListPresentationStyles.singleLine;

    private overrideSelectedIndex: boolean;

    public detailsList: IDetailsList;

    constructor(props: IVssDetailsListProps & IDetailsListProps) {
        super(props);

        this.state = {};
    }

    public componentWillMount() {
        const classNames = this._buildClassNames(this.props);
        const columns = this._buildColumns(this.props);
        const selection = this._buildSelection(this.props);
        this.overrideSelectedIndex = false;
        this.setState({ classNames, columns, selection });
    }

    public componentDidMount() {
        this.overrideSelectedIndex = true;
    }

    public componentWillReceiveProps(nextProps: IVssDetailsListProps) {
        const newState: IVssDetailsListState = {};
        if (
            this.props.className !== nextProps.className ||
            this.props.presentationStyles !== nextProps.presentationStyles ||
            this.props.usePresentationStyles !== nextProps.usePresentationStyles ||
            this.props.rowStyle !== nextProps.rowStyle
        ) {
            newState.classNames = this._buildClassNames(nextProps);
        }
        if (this.props.columns !== nextProps.columns || this.props.actionsColumnKey !== nextProps.actionsColumnKey) {
            newState.columns = this._buildColumns(nextProps);
        }
        if (this.props.selection !== nextProps.selection) {
            newState.selection = this._buildSelection(nextProps);
        }

        this.setState(newState);
    }

    public render(): JSX.Element {
        const { checkboxVisibility = CheckboxVisibility.hidden } = this.props;
        const { classNames, columns } = this.state;
        const props = { ...this.props };

        const hasActions = this._hasActions(this.props);

        props.className = classNames;
        props.checkboxVisibility = checkboxVisibility;
        props.checkboxCellClassName = "vss-DetailsList--cellCheck";
        props.columns = columns;
        if (hasActions) {
            props.onItemContextMenu = this._onItemContextMenu;
        }

        const detailsList = (
            <DetailsList
                {...props}
                initialFocusedIndex={this.overrideSelectedIndex ? undefined : props.initialFocusedIndex}
                selection={this.state.selection}
                componentRef={this._resolveRef("detailsList")}
            />
        );

        if (hasActions) {
            return (
                <div className="vss-DetailsList--contextmenu-wrapper">
                    {detailsList}
                    {this._getContextMenu()}
                </div>
            );
        }

        return detailsList;
    }

    private _onItemContextMenu = (item: any, index: number, ev: Event): void | boolean => {
        const mouseEvent = ev as MouseEvent;

        if (mouseEvent) {
            // We don't want to show the custom contextual menu or prevent default
            // if the target element is an anchor tag.
            const target = mouseEvent.target as HTMLElement;
            if (target && (target.nodeName === "A" || closest(target, "A"))) {
                return true;
            }
        }

        if (this.props.shouldDisplayActions && !this.props.shouldDisplayActions(item)) {
            return true;
        }

        if (!mouseEvent) {
            return this.setState({ activatedItem: item, target: undefined });
        }

        let target: MouseEvent | HTMLElement = null;
        // For chrome/firefox mouseEvent.button === 0 can be used to assume keyboard interaction here, whereas
        // in IE the mouseEvent.button will be 2, but the clientX or clientY has a negative value.
        // So for IE and Edge, an HTMLElement needs to be passed in for anchoring otherwise the menu will be at (0, 0).
        if (mouseEvent.button === 0 || !(mouseEvent.clientX > 0 && mouseEvent.clientY > 0)) {
            const targetFromEvent = mouseEvent.target as HTMLElement;
            const row: Element = closest(targetFromEvent, '[role="row"]');
            target = row ? row.querySelector<HTMLElement>(`.${ContextualMenuButton.ButtonContainerClassName}`) : null;
        }

        this.setState({ activatedItem: item, target: target || mouseEvent });
    };

    private _onDismiss = (): void => {
        this.setState({ activatedItem: undefined, target: undefined });
    };

    private _onEllipsisClick = (e: React.MouseEvent<HTMLElement>): void => {
        // Important: to handle the toggle modifiers for multi-select scenario, we must update the selection here prior to calling getMenuItems, otherwise
        // getMenuItems may be looking an outdated selection object and returning items not in context of the updated selections. Unfortunately, even if the click
        // event is allowed to propagate down to DetailsList for automatic selection handling, getMenuItems for the ActionsButton would have been called already
        // (that's why selection needs to be updated here and also by design the ActionsButton should ignore toggle modifier if row is already selected).
        const index = Number(e.currentTarget.dataset.index);
        const isMultipleSelect = this.props.selectionMode === SelectionMode.multiple;
        const selection = this.state.selection;

        selection.setChangeEvents(false);
        const isToggleModifierPressed = e.ctrlKey || e.metaKey;
        if (isMultipleSelect && e.shiftKey) {
            const clearExistingSelection = !isToggleModifierPressed;
            selection.selectToIndex(index, clearExistingSelection);
        } else if (isMultipleSelect) {
            const isNewSelection = !selection.isIndexSelected(index);
            if (isNewSelection && !isToggleModifierPressed) {
                selection.setAllSelected(false);
            }
            selection.setIndexSelected(index, true, true);
        } else {
            selection.setAllSelected(false);
            selection.setIndexSelected(index, true, true);
        }
        selection.setChangeEvents(true);
    };

    private _createActionsColumn(column: IColumn): IColumn {
        const wrappedColumn: IColumn = { ...column };

        wrappedColumn.onRender = (item: any, index: number, c: IColumn) => {
            let originalContent: React.ReactElement<{}> | null = null;

            if (column.onRender) {
                originalContent = column.onRender(item, index, c);
            } else if (this.props.onRenderItemColumn) {
                originalContent = this.props.onRenderItemColumn(item, index, c);
            } else {
                // What should we do if they provided a column that doesn't have a
                // custom onRender method? The same thing as the base row will do?
            }

            let shouldDisplayActions = true;
            if (this.props.shouldDisplayActions) {
                shouldDisplayActions = this.props.shouldDisplayActions(item);
            }

            const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.which === KeyCode.escape) {
                    e.stopPropagation();
                }
            };

            if (shouldDisplayActions) {
                return (
                    <div className="actions-column-wrapper" onKeyDown={onKeyDown}>
                        <div className="original-content">{originalContent}</div>
                        <MoreActionsButton
                            allocateSpaceWhileHidden={this.props.allocateSpaceForActionsButtonWhileHidden}
                            className="vss-DetailsList--contextual-menu-button"
                            getItems={this.props.getMenuItems && (() => this.props.getMenuItems(item))}
                            getItemProviders={this.props.getMenuItemProviders && (() => this.props.getMenuItemProviders(item))}
                            onDismiss={this._onDismiss}
                            dataIndex={index}
                            onClick={this._onEllipsisClick}
                        />
                    </div>
                );
            } else {
                return originalContent;
            }
        };

        return wrappedColumn;
    }

    private _hasActions(props: IVssDetailsListProps): boolean {
        return !!(props.actionsColumnKey && (props.getMenuItems || props.getMenuItemProviders));
    }

    private _buildSelection(props: IVssDetailsListProps): ISelection {
        return props.selection || new Selection({ getKey: props.getKey });
    }

    private _buildColumns(props: IVssDetailsListProps): IColumn[] {
        const { actionsColumnKey, columns } = props;

        if (this._hasActions(props) && columns) {
            const modifiedColumns: IColumn[] = [];
            for (let column of columns) {
                if (column.key === actionsColumnKey) {
                    column = this._createActionsColumn(column);
                }
                modifiedColumns.push(column);
            }
            return modifiedColumns;
        }

        return columns;
    }

    private _buildClassNames(props: IVssDetailsListProps): string {
        const { usePresentationStyles, className, rowStyle, hideGroupExpansion } = props;
        let { presentationStyles } = props;
        let classNames = "";

        if (usePresentationStyles || presentationStyles) {
            if (presentationStyles === undefined) {
                presentationStyles = this.standardPresentationStyle;
            }

            const classNamesList = ["vss-DetailsList--layout"];
            if (className) {
                classNamesList.push(className);
            }
            if (presentationStyles & VssDetailsListPresentationStyles.hideActionsButton) {
                classNamesList.push("hideActionsButton");
            }
            if (presentationStyles & VssDetailsListPresentationStyles.singleLine) {
                classNamesList.push("singleLine");
            }
            if (presentationStyles & VssDetailsListPresentationStyles.flexCellLayout) {
                classNamesList.push("flexCellLayout");
            }
            if (presentationStyles & VssDetailsListPresentationStyles.paddingInCells) {
                classNamesList.push("paddingInCells");
            }
            if (presentationStyles & VssDetailsListPresentationStyles.colors) {
                classNamesList.push("colors");
            }
            if (presentationStyles & VssDetailsListPresentationStyles.linkStyles) {
                classNamesList.push("linkStyles");
            }
            if (presentationStyles & VssDetailsListPresentationStyles.headerRowStyles) {
                classNamesList.push("headerRowStyles");
            }

            classNames = classNamesList.join(" ");
        } else {
            classNames = css("vss-DetailsList", className, rowStyle === VssDetailsListRowStyle.twoLine && "vss-DetailsList--twoLine");
        }

        classNames = css(classNames, hideGroupExpansion && "noGroupExpand");

        return classNames;
    }

    private _getContextMenu(): JSX.Element {
        const { activatedItem, target } = this.state;
        if (!activatedItem) {
            return undefined;
        }

        const { getMenuItems, getMenuItemProviders } = this.props;
        const items: IContextualMenuItem[] = getMenuItems ? getMenuItems(activatedItem) : undefined;
        const itemProviders = getMenuItemProviders ? getMenuItemProviders(activatedItem) : undefined;
        if (!items && !itemProviders) {
            return undefined;
        }

        return (
            <VssContextualMenu
                items={items}
                providers={itemProviders}
                onDismiss={this._onDismiss}
                shouldFocusOnMount={true}
                target={target}
                gapSpace={0}
            />
        );
    }
}
